import type { EntityResolvers } from '@terra/ui-design-system';
import { useMemo } from 'react';
import { useCredentials } from './api/useCredentials';
import { useEndpointsPreview } from './api/useEndpointsPreview';
import { useSignalsPreview } from './api/useSignalsPreview';

export function useTraceEntityResolvers(
  applicationId: string,
): EntityResolvers {
  const { credentials } = useCredentials({ applicationId });
  const { endpoints } = useEndpointsPreview({ applicationId });
  const { signals } = useSignalsPreview({ applicationId });

  return useMemo(
    () => ({
      credential: (id) => {
        const credential = credentials.find((cred) => cred.id === id);

        if (!credential) return undefined;

        return {
          id: credential.id,
          name:
            credential.username || `Credential ${credential.id.slice(0, 8)}`,
          type: credential.role || 'unknown',
          role: credential.role || undefined,
          username: credential.username,
        };
      },
      endpoint: (id) => {
        const endpoint = endpoints.find((ep) => ep.id === id);

        if (!endpoint) return undefined;

        return {
          id: endpoint.id,
          path: endpoint.path || '',
          host: endpoint.host || '',
          method: endpoint.request?.method || '',
        };
      },
      signal: (id) => {
        const signal = signals.find((sig) => sig.id === id);

        if (!signal) return undefined;

        return {
          id: signal.id,
          category: signal.category || '',
          description: signal.description || signal.analysis || '',
        };
      },
      application: (id) =>
        id === applicationId ? { id, name: 'Current Application' } : undefined,
    }),
    [applicationId, credentials, endpoints, signals],
  );
}
